import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import CompanySchema, { CompanyJSON, CompanyParams } from "marketplace/companies/company-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";

export default class Company {
  public static get EXAMPLE(): Company {
    return new Company({
      id: Guid.generate(),
      legalEntityId: Guid.generate(),
      profile: CompanyProfile.EXAMPLE,
      contactId: Guid.generate(),
      isVisible: true,
    });
  }

  public static get Prototype(): Company {
    return new Company({
      id: Guid.generate(),
      legalEntityId: Guid.generate(),
    });
  }

  public static create(values: CompanyParams): Company {
    const parsed = CompanySchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid Company parameters.");
    }
    return new Company(parsed);
  }

  public static fromJSON(json: CompanyJSON): Company {
    if (isNullOrUndefined(json)) return json;
    const params = CompanySchema.deserialize(json);
    return new Company(params);
  }

  public static fromJSONArray(jsonArray: CompanyJSON[]): Company[] {
    if (isNullOrUndefined(jsonArray)) return jsonArray;
    return jsonArray.map((json) => Company.fromJSON(json));
  }

  public readonly id: Guid;
  public readonly legalEntityId: Guid;
  public readonly profile?: CompanyProfile;
  public readonly contactId?: Guid;

  public constructor(
    values: CompanyParams & { dependencies?: { store?: CompanyStore } }
  ) {
    this.id = values.id;
    this.legalEntityId = values.legalEntityId;
    this.profile = values.profile ? CompanyProfile.create(values.profile) : undefined;
    this.contactId = values.contactId;
    this._isVisible = values.isVisible ?? false;
    this._store = values.dependencies?.store;
    this._originalState = this.toJSON();
  }

  /** A company isVisible if it can be searched in the marketplace.*/
  public get isVisible(): boolean {
    return this._isVisible;
  }
  public get store(): CompanyStore | undefined {
    return this._store;
  }
  /** Creates a copy of the store to prevent mutation. */
  public set store(store: CompanyStore | undefined) {
    this._store = store ? { ...store } : undefined;
  }
  public toJSON() {
    return CompanySchema.serialize(this);
  }

  public isEqualTo(other: Company): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toString(): string {
    return `Company (
      id: ${this.id}
      legalEntityId: ${this.legalEntityId}
      profile: ${this.profile}
      contactId: ${this.contactId}
      isVisible: ${this.isVisible}
    )`;
  }

  /**
   * Toggles the @field isVisible of the company.
   * @returns The new visibility state of the company.
   */
  public toggleVisibility = (): boolean => (this._isVisible = !this._isVisible);

  public async saveChanges(): Promise<Company> {
    if (!this._store) throw new Error("Company is not attached to a store");

    if (JSON.stringify(this._originalState) === JSON.stringify(this.toJSON())) {
      console.warn("No changes detected in Company. Skipping save.");
      return this;
    }

    return await this._store.saveCompany(this);
  }

  private readonly _originalState: CompanyJSON;
  private _store?: CompanyStore;
  private _isVisible: boolean;
}

export interface CompanyStore {
  saveCompany(company: Company): Promise<Company>;
}
